import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useStore, put, remove, generateId, uploadGalleryImage, deleteGalleryImage } from '../hooks/useDB';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';
import CinematicPlayer from '../components/CinematicPlayer';
import {
  Upload, X, ChevronLeft, ChevronRight, Play, Pause,
  Trash2, Images, Film, Plus, Calendar, Camera, FolderOpen,
  Maximize2, Minimize2, Grid, LayoutGrid, Rows
} from 'lucide-react';

const SLIDE_DURATION = 6000;
const MAX_FILE_MB = 8;
const CONTROLS_HIDE_MS = 3000;
const MAX_BATCH = 20;
const KB_VARIANTS = ['kenBurnsA', 'kenBurnsB', 'kenBurnsC'];

function fmtDate(d) {
  if (!d || d === 'Date Unknown') return '';
  try {
    return new Date(d + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
}

// Photos store a Supabase Storage URL (imageUrl); a few legacy rows may still
// carry an inline base64 string (imageDataUrl). Support both when rendering.
function imgSrc(photo) {
  return photo?.imageUrl || photo?.imageDataUrl || '';
}

export default function Gallery({ currentUser }) {
  const isAdmin = currentUser?.role === 'admin';

  const { data: rawPhotos, loading } = useStore('gallery');
  const photos = useMemo(() => [...rawPhotos].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)), [rawPhotos]);
  const [activeEvent, setActiveEvent] = useState('all');

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchEvent, setBatchEvent] = useState('');
  const [batchDate, setBatchDate] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(0);

  // Cinematic state
  const [cinematicIdx, setCinematicIdx] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [webglFailed, setWebglFailed] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [layoutMode, setLayoutMode] = useState('grid'); // 'grid', 'justified', 'masonry'
  const [fitMode, setFitMode] = useState('contain'); // 'contain' (best fit, see whole image), 'cover' (fill screen)

  const fileInputRef = useRef(null);
  const progressRafRef = useRef(null);
  const slideStartRef = useRef(null);
  const pausedProgressRef = useRef(0);
  const hideTimerRef = useRef(null);

  // Lock body scroll in cinematic/slideshow mode
  useEffect(() => {
    if (cinematicIdx !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [cinematicIdx]);

  const eventMap = useMemo(() => {
    const map = {};
    photos.forEach(p => {
      const ev = p.eventName || 'General';
      if (!map[ev]) map[ev] = [];
      map[ev].push(p);
    });
    return map;
  }, [photos]);

  const eventNames = useMemo(() =>
    Object.entries(eventMap)
      .sort((a, b) => new Date(b[1][0]?.uploadedAt || 0) - new Date(a[1][0]?.uploadedAt || 0))
      .map(([name]) => name),
    [eventMap]
  );

  const visiblePhotos = useMemo(() =>
    activeEvent === 'all' ? photos : (eventMap[activeEvent] || []),
    [photos, eventMap, activeEvent]
  );

  const groupByDate = useCallback((arr) => {
    const byDate = {};
    arr.forEach(p => {
      const key = p.takenDate || p.uploadedAt?.slice(0, 10) || 'Date Unknown';
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(p);
    });
    return Object.entries(byDate)
      .sort((a, b) => (b[0] < a[0] ? -1 : b[0] > a[0] ? 1 : 0))
      .map(([date, items]) => ({ date, items }));
  }, []);

  const displayGroups = useMemo(() => {
    if (activeEvent === 'all') {
      return eventNames.map(name => ({
        eventName: name,
        photoCount: eventMap[name].length,
        dateGroups: groupByDate(eventMap[name]),
      }));
    }
    return [{
      eventName: activeEvent,
      photoCount: (eventMap[activeEvent] || []).length,
      dateGroups: groupByDate(eventMap[activeEvent] || []),
    }];
  }, [activeEvent, eventMap, eventNames, groupByDate]);

  // ── Cinematic controls ──────────────────────────────────────────────────────
  const pokeControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
  }, []);

  const startProgress = useCallback(() => {
    if (progressRafRef.current) cancelAnimationFrame(progressRafRef.current);
    slideStartRef.current = performance.now() - pausedProgressRef.current * SLIDE_DURATION / 100;
    const tick = (now) => {
      const elapsed = now - slideStartRef.current;
      const pct = Math.min((elapsed / SLIDE_DURATION) * 100, 100);
      setProgress(pct);
      if (pct < 100) {
        progressRafRef.current = requestAnimationFrame(tick);
      } else {
        pausedProgressRef.current = 0;
        setCinematicIdx(prev => (prev + 1) % visiblePhotos.length);
      }
    };
    progressRafRef.current = requestAnimationFrame(tick);
  }, [visiblePhotos.length]);

  const stopProgress = useCallback(() => {
    if (progressRafRef.current) cancelAnimationFrame(progressRafRef.current);
  }, []);

  useEffect(() => {
    if (cinematicIdx === null) return;
    pausedProgressRef.current = 0;
    setProgress(0);
    if (isPlaying) startProgress();
    return () => stopProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cinematicIdx]);

  useEffect(() => {
    if (cinematicIdx === null) return;
    if (isPlaying) startProgress();
    else { pausedProgressRef.current = progress; stopProgress(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  const closeCinematic = useCallback(() => {
    stopProgress();
    setCinematicIdx(null);
    setIsPlaying(true);
    setProgress(0);
    pausedProgressRef.current = 0;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, [stopProgress]);

  const openCinematic = useCallback((idx) => {
    setCinematicIdx(idx);
    setIsPlaying(true);
    pokeControls();
  }, [pokeControls]);

  useEffect(() => {
    if (cinematicIdx === null) return;
    const handler = (e) => {
      pokeControls();
      if (e.key === 'Escape') { closeCinematic(); return; }
      if (e.key === 'ArrowRight') { pausedProgressRef.current = 0; setProgress(0); setCinematicIdx(p => (p + 1) % visiblePhotos.length); }
      if (e.key === 'ArrowLeft') { pausedProgressRef.current = 0; setProgress(0); setCinematicIdx(p => (p - 1 + visiblePhotos.length) % visiblePhotos.length); }
      if (e.key === ' ') { e.preventDefault(); setIsPlaying(p => !p); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cinematicIdx, visiblePhotos.length, closeCinematic, pokeControls]);

  const goTo = (dir) => {
    pausedProgressRef.current = 0;
    setProgress(0);
    setCinematicIdx(prev => (prev + dir + visiblePhotos.length) % visiblePhotos.length);
  };

  // ── Multi-file upload ───────────────────────────────────────────────────────
  const processFiles = useCallback(async (files) => {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/')).slice(0, MAX_BATCH);
    const tooBig = arr.filter(f => f.size > MAX_FILE_MB * 1024 * 1024);
    if (tooBig.length > 0) setUploadError(`${tooBig.length} file(s) exceed ${MAX_FILE_MB}MB and were skipped.`);
    const valid = arr.filter(f => f.size <= MAX_FILE_MB * 1024 * 1024);

    const batch = await Promise.all(valid.map(file => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = ev => resolve({
        id: generateId(),
        file,
        previewUrl: ev.target.result,
        title: file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        caption: '',
      });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    })));

    setBatchFiles(prev => [...prev, ...batch]);
    if (tooBig.length === 0) setUploadError('');
  }, []);

  const handleFilesSelected = (e) => { if (e.target.files?.length) processFiles(e.target.files); };
  const handleDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files); };
  const handleDragOver = (e) => e.preventDefault();

  const handleUpload = async () => {
    if (batchFiles.length === 0) { setUploadError('Please select at least one photo.'); return; }
    if (!batchEvent.trim()) { setUploadError('Event / album name is required.'); return; }
    setUploading(true);
    setUploadDone(0);
    setUploadError('');
    try {
      for (let i = 0; i < batchFiles.length; i++) {
        const f = batchFiles[i];
        const record = {
          id: f.id,
          title: f.title.trim() || `Photo ${i + 1}`,
          caption: f.caption.trim() || f.title.trim() || `From ${batchEvent.trim()}`,
          eventName: batchEvent.trim(),
          takenDate: batchDate || new Date().toISOString().slice(0, 10),
          uploadedAt: new Date().toISOString(),
          uploadedBy: currentUser?.name || currentUser?.email || 'Admin',
        };
        // Upload the image file to Supabase Storage; the table row only holds
        // the small public URL, so it saves reliably and is shared everywhere.
        const { url, path } = await uploadGalleryImage(f.file, f.id);
        record.imageUrl = url;
        record.storagePath = path;
        await put('gallery', record);
        setUploadDone(i + 1);
      }
      toast.success(`${batchFiles.length} photo${batchFiles.length > 1 ? 's' : ''} added`);
      setShowUpload(false);
      setBatchFiles([]);
      setBatchEvent('');
      setBatchDate('');
      window.dispatchEvent(new CustomEvent('planner:data-change'));
    } catch (err) {
      const msg = err?.message || 'Upload failed. Please try again.';
      setUploadError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photo) => {
    const ok = await confirm(`Delete "${photo.title}" from the gallery?`, 'Delete Photo');
    if (!ok) return;
    try {
      await remove('gallery', photo.id);
      if (photo.storagePath) await deleteGalleryImage(photo.storagePath);
      toast.success('Photo deleted');
      window.dispatchEvent(new CustomEvent('planner:data-change'));
    } catch { toast.error('Failed to delete photo'); }
  };

  const cancelUpload = () => {
    setShowUpload(false);
    setBatchFiles([]);
    setBatchEvent('');
    setBatchDate('');
    setUploadError('');
  };

  const renderPhotoTile = (photo, localIdx, globalIdx, variant) => {
    let tileStyle = {
      position: 'relative',
      cursor: 'pointer',
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 4px 18px rgba(0,0,0,0.06)',
      display: 'block',
      animation: 'galleryReveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) both',
      animationDelay: `${Math.min(localIdx * 40, 400)}ms`,
    };

    if (variant === 'grid') {
      tileStyle.aspectRatio = '1 / 1';
      tileStyle.width = '100%';
    } else if (variant === 'justified') {
      tileStyle.height = '220px';
      tileStyle.flex = '1 1 auto';
      tileStyle.minWidth = '160px';
      tileStyle.maxWidth = '365px';
    } else {
      // masonry layout context
      tileStyle.breakInside = 'avoid';
      tileStyle.marginBottom = 14;
    }

    return (
      <div
        key={photo.id}
        className="gallery-tile animate-blur-in"
        style={tileStyle}
        onClick={() => openCinematic(globalIdx >= 0 ? globalIdx : 0)}
      >
        <img
          src={imgSrc(photo)}
          alt={photo.title}
          loading="lazy"
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
        />
        {/* Hover translucent gradient */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 65%, transparent 100%)', pointerEvents: 'none' }} className="gallery-overlay" />
        
        <div className="gallery-tile-info" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 14px 12px', pointerEvents: 'none', zIndex: 2 }}>
          <div className="gallery-tile-title" style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3, textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
            {photo.title}
          </div>
          {photo.takenDate && (
            <div className="gallery-tile-date" style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
              <Calendar size={9} /> {fmtDate(photo.takenDate)}
            </div>
          )}
        </div>
        {isAdmin && (
          <button
            className="gallery-del"
            onClick={e => { e.stopPropagation(); handleDelete(photo); }}
            title="Delete photo"
            style={{ position: 'absolute', top: 10, right: 10, width: 30, height: 30, minHeight: 30, borderRadius: 8, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', zIndex: 10 }}
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    );
  };

  const currentPhoto = cinematicIdx !== null ? visiblePhotos[cinematicIdx] : null;
  const kbVariant = cinematicIdx !== null ? KB_VARIANTS[cinematicIdx % KB_VARIANTS.length] : 'kenBurnsA';
  const controlFade = { opacity: controlsVisible ? 1 : 0, transition: 'opacity 0.4s ease', pointerEvents: controlsVisible ? 'auto' : 'none' };

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Event Gallery</div>
          <div className="page-subtitle">
            {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
            {eventNames.length > 0 && ` across ${eventNames.length} ${eventNames.length === 1 ? 'event' : 'events'}`}
            {' '}· click any photo to enter cinematic story mode
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {visiblePhotos.length > 0 && (
            <button className="btn btn-secondary" onClick={() => openCinematic(0)}>
              <Film size={15} /> Play Slideshow
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <Upload size={15} /> Upload Photos
            </button>
          )}
        </div>
      </div>

      {/* Filters and Layout controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
        {/* Event filter tabs (Left) */}
        {eventNames.length > 1 ? (
          <div className="sh-tabs-list" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
            <button
              className={`sh-tabs-trigger ${activeEvent === 'all' ? 'active' : ''}`}
              onClick={() => setActiveEvent('all')}
            >
              <FolderOpen size={13} />
              All Events
              <span style={{
                background: activeEvent === 'all' ? 'rgba(249,101,22,0.15)' : '#e4e4e7',
                color: activeEvent === 'all' ? 'var(--accent)' : '#71717a',
                borderRadius: 99, padding: '1px 6px', fontSize: 10, fontWeight: 700,
              }}>
                {photos.length}
              </span>
            </button>
            {eventNames.map(name => (
              <button
                key={name}
                className={`sh-tabs-trigger ${activeEvent === name ? 'active' : ''}`}
                onClick={() => setActiveEvent(name)}
              >
                <Camera size={13} />
                {name}
                <span style={{
                  background: activeEvent === name ? 'rgba(249,101,22,0.15)' : '#e4e4e7',
                  color: activeEvent === name ? 'var(--accent)' : '#71717a',
                  borderRadius: 99, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                }}>
                  {eventMap[name]?.length || 0}
                </span>
              </button>
            ))}
          </div>
        ) : <div />}

        {/* Layout Mode controls (Right) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface2)', padding: 4, borderRadius: 10, border: '1px solid var(--border-solid)', marginLeft: activeEvent === 'all' && eventNames.length <= 1 ? 'auto' : 0 }}>
          <button
            title="Clean Square Grid (Google Photos style)"
            className={`btn btn-sm ${layoutMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '6px 12px', border: 'none', background: layoutMode === 'grid' ? 'var(--accent)' : 'transparent', color: layoutMode === 'grid' ? '#fff' : 'var(--text2)', boxShadow: 'none', fontSize: 12, display: 'flex', alignItems: 'center' }}
            onClick={() => setLayoutMode('grid')}
          >
            <Grid size={13} style={{ marginRight: 4 }} /> Square Grid
          </button>
          <button
            title="Justified Aspect-Ratio Handheld Rows"
            className={`btn btn-sm ${layoutMode === 'justified' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '6px 12px', border: 'none', background: layoutMode === 'justified' ? 'var(--accent)' : 'transparent', color: layoutMode === 'justified' ? '#fff' : 'var(--text2)', boxShadow: 'none', fontSize: 12, display: 'flex', alignItems: 'center' }}
            onClick={() => setLayoutMode('justified')}
          >
            <Rows size={13} style={{ marginRight: 4 }} /> Justified
          </button>
          <button
            title="Masonry Auto Columns"
            className={`btn btn-sm ${layoutMode === 'masonry' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ padding: '6px 12px', border: 'none', background: layoutMode === 'masonry' ? 'var(--accent)' : 'transparent', color: layoutMode === 'masonry' ? '#fff' : 'var(--text2)', boxShadow: 'none', fontSize: 12, display: 'flex', alignItems: 'center' }}
            onClick={() => setLayoutMode('masonry')}
          >
            <LayoutGrid size={13} style={{ marginRight: 4 }} /> Masonry
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '80px 20px' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--border-solid)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ color: 'var(--text2)', fontSize: 14 }}>Loading gallery...</span>
        </div>
      ) : photos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><Images size={52} /></div>
          <h3>No photos yet</h3>
          <p style={{ color: 'var(--text2)', marginTop: 8, maxWidth: 380, margin: '8px auto 0', lineHeight: 1.6 }}>
            {isAdmin
              ? 'Upload event photos to start building your cinematic story gallery. Organize them by event and date for the best experience.'
              : 'No event photos have been uploaded yet. Check back after the event!'}
          </p>
          {isAdmin && (
            <button className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => setShowUpload(true)}>
              <Upload size={14} /> Upload First Photos
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 44 }}>
          {displayGroups.map(group => (
            <div key={group.eventName}>
              {/* Event section header */}
              <div className="gallery-event-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div className="gallery-event-icon">
                    <Camera size={18} />
                  </div>
                  <div>
                    <div className="gallery-event-name">{group.eventName}</div>
                    <div className="gallery-event-meta">
                      {group.photoCount} {group.photoCount === 1 ? 'photo' : 'photos'}
                      {group.dateGroups.length > 0 && group.dateGroups[0].date !== 'Date Unknown' && (
                        <> · <Calendar size={10} style={{ verticalAlign: 'middle', display: 'inline' }} /> {fmtDate(group.dateGroups[0].date)}</>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {activeEvent === 'all' && (
                    <button className="btn btn-sm btn-secondary" onClick={() => setActiveEvent(group.eventName)}>
                      View All
                    </button>
                  )}
                  <button className="btn btn-sm btn-secondary" onClick={() => {
                    if (activeEvent !== group.eventName) setActiveEvent(group.eventName);
                    openCinematic(0);
                  }}>
                    <Film size={13} /> Slideshow
                  </button>
                </div>
              </div>

              {/* Date groups within event */}
              {group.dateGroups.map(({ date, items }) => (
                <div key={date} style={{ marginTop: 24 }}>
                  {(group.dateGroups.length > 1 || (date !== 'Date Unknown' && activeEvent !== 'all')) && (
                    <div className="gallery-date-header">
                      <Calendar size={11} />
                      {fmtDate(date) || date}
                      <span style={{ color: 'var(--text3)', fontWeight: 400, marginLeft: 4 }}>· {items.length}</span>
                    </div>
                  )}

                  {/* Layout mode switcher */}
                  {layoutMode === 'masonry' ? (
                    <div style={{ columns: 'auto 260px', columnGap: 14, columnFill: 'balance', marginTop: 12 }}>
                      {items.map((photo, localIdx) => {
                        const globalIdx = visiblePhotos.findIndex(p => p.id === photo.id);
                        return renderPhotoTile(photo, localIdx, globalIdx, 'masonry');
                      })}
                    </div>
                  ) : layoutMode === 'justified' ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
                      {items.map((photo, localIdx) => {
                        const globalIdx = visiblePhotos.findIndex(p => p.id === photo.id);
                        return renderPhotoTile(photo, localIdx, globalIdx, 'justified');
                      })}
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginTop: 12 }}>
                      {items.map((photo, localIdx) => {
                        const globalIdx = visiblePhotos.findIndex(p => p.id === photo.id);
                        return renderPhotoTile(photo, localIdx, globalIdx, 'grid');
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && cancelUpload()}>
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Upload Photos</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                  Add multiple photos to an event album at once
                </div>
              </div>
              <button className="btn btn-icon btn-secondary" onClick={cancelUpload}><X size={18} /></button>
            </div>
            <div className="modal-body">

              {/* Batch settings */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Event / Album Name *</label>
                  <input
                    value={batchEvent}
                    onChange={e => setBatchEvent(e.target.value)}
                    placeholder="e.g. Annual Offsite 2026"
                    list="gallery-event-suggestions"
                  />
                  <datalist id="gallery-event-suggestions">
                    {eventNames.map(n => <option key={n} value={n} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Date Taken</label>
                  <input
                    type="date"
                    value={batchDate}
                    onChange={e => setBatchDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                  />
                </div>
              </div>

              {/* Drop zone or file grid */}
              {batchFiles.length === 0 ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                  className="gallery-drop-zone"
                >
                  <div style={{ width: 56, height: 56, background: 'rgba(249,101,22,0.1)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}>
                    <Upload size={26} />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Drop photos here or click to browse</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                      Select multiple photos at once · Max {MAX_FILE_MB}MB each · Up to {MAX_BATCH} photos
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {batchFiles.length} {batchFiles.length === 1 ? 'photo' : 'photos'} selected
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => fileInputRef.current?.click()}>
                        <Plus size={12} /> Add More
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => setBatchFiles([])}>
                        Clear All
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, maxHeight: 320, overflowY: 'auto' }}>
                    {batchFiles.map((item, idx) => (
                      <div key={item.id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-solid)', background: '#f9f9fb' }}>
                        <img src={item.previewUrl} alt={item.title} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                        <div style={{ padding: '7px 7px 5px' }}>
                          <input
                            value={item.title}
                            onChange={e => setBatchFiles(prev => prev.map((f, i) => i === idx ? { ...f, title: e.target.value } : f))}
                            placeholder="Photo title"
                            style={{ width: '100%', fontSize: 11, padding: '3px 6px', borderRadius: 5, border: '1px solid var(--border-solid)', minHeight: 'auto', background: '#fff', boxSizing: 'border-box' }}
                          />
                        </div>
                        <button
                          onClick={() => setBatchFiles(prev => prev.filter((_, i) => i !== idx))}
                          style={{ position: 'absolute', top: 5, right: 5, width: 22, height: 22, minHeight: 22, background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: 5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                        >
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFilesSelected} />

              {uploadError && (
                <div style={{ fontSize: 13, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px' }}>
                  {uploadError}
                </div>
              )}

              {uploading && (
                <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                    Uploading {uploadDone} of {batchFiles.length}…
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${(uploadDone / batchFiles.length) * 100}%` }} />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={cancelUpload} disabled={uploading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || batchFiles.length === 0} style={{ opacity: (uploading || batchFiles.length === 0) ? 0.6 : 1 }}>
                {uploading
                  ? `Uploading ${uploadDone}/${batchFiles.length}…`
                  : <><Upload size={14} /> Upload {batchFiles.length > 0 ? `${batchFiles.length} ` : ''}Photos</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cinematic Story Mode — full-bleed immersive viewer */}
      {currentPhoto && (
        <div
          style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 9000, userSelect: 'none', cursor: controlsVisible ? 'default' : 'none' }}
          onMouseMove={pokeControls}
          onTouchStart={pokeControls}
        >
          {/* Full-screen image fills entire viewport */}
          {!webglFailed ? (
            <CinematicPlayer src={imgSrc(currentPhoto)} fitMode={fitMode} onFallback={() => setWebglFailed(true)} />
          ) : (
            <img
              key={cinematicIdx}
              src={imgSrc(currentPhoto)}
              alt={currentPhoto.title}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: fitMode,
                animation: fitMode === 'contain' ? 'none' : `${kbVariant} ${SLIDE_DURATION}ms ease-out both`
              }}
            />
          )}

          {/* Radial vignette — depth without letterboxes */}
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 55%, transparent 30%, rgba(0,0,0,0.45) 100%)', pointerEvents: 'none', zIndex: 1 }} />

          {/* ── TOP: story-progress segments + event label + close ── */}
          <div style={{
            ...controlFade,
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 4,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 72%, transparent 100%)',
            padding: '16px 20px 40px',
          }}>
            {/* Per-photo story segments (Instagram-style) */}
            <div style={{ display: 'flex', gap: 3, marginBottom: 14 }}>
              {visiblePhotos.length <= 30
                ? visiblePhotos.map((_, i) => (
                    <div key={i} style={{ flex: 1, height: 2.5, background: 'rgba(255,255,255,0.22)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: i < cinematicIdx ? '100%' : i === cinematicIdx ? `${progress}%` : '0%',
                        background: '#fff',
                      }} />
                    </div>
                  ))
                : (
                  <div style={{ flex: 1, height: 2.5, background: 'rgba(255,255,255,0.22)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${((cinematicIdx + progress / 100) / visiblePhotos.length) * 100}%`, background: '#fff' }} />
                  </div>
                )
              }
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {currentPhoto.eventName && currentPhoto.eventName !== 'General' && (
                  <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                    {currentPhoto.eventName}
                  </span>
                )}
                <span style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 600, padding: '2px 9px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.12)' }}>
                  {cinematicIdx + 1} / {visiblePhotos.length}
                </span>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Fit Mode Toggle Button */}
                <button
                  onClick={() => setFitMode(prev => prev === 'contain' ? 'cover' : 'contain')}
                  title={fitMode === 'contain' ? "Switch to Fill Screen" : "Switch to Fit Screen"}
                  style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, color: 'rgba(255,255,255,0.85)', cursor: 'pointer', width: 36, height: 36, minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {fitMode === 'contain' ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                </button>
                {/* Close Button */}
                <button
                  onClick={closeCinematic}
                  style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10, color: 'rgba(255,255,255,0.85)', cursor: 'pointer', width: 36, height: 36, minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* ── LEFT navigation zone (full height, wide tap target) ── */}
          <button
            onClick={() => goTo(-1)}
            style={{
              ...controlFade,
              position: 'absolute', left: 0, top: 0, bottom: 0, width: '18%',
              background: 'transparent', border: 'none', cursor: 'pointer', zIndex: 3,
              display: 'flex', alignItems: 'center', paddingLeft: 16,
            }}
          >
            <div style={{ width: 44, height: 44, background: 'rgba(0,0,0,0.28)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.13)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <ChevronLeft size={22} />
            </div>
          </button>

          {/* ── RIGHT navigation zone ── */}
          <button
            onClick={() => goTo(1)}
            style={{
              ...controlFade,
              position: 'absolute', right: 0, top: 0, bottom: 0, width: '18%',
              background: 'transparent', border: 'none', cursor: 'pointer', zIndex: 3,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 16,
            }}
          >
            <div style={{ width: 44, height: 44, background: 'rgba(0,0,0,0.28)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.13)', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <ChevronRight size={22} />
            </div>
          </button>

          {/* ── BOTTOM: title + caption + meta + play control ── */}
          <div style={{
            ...controlFade,
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 4,
            background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)',
            padding: '64px 26px 30px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Photo title */}
                <div
                  key={`title-${cinematicIdx}`}
                  style={{ fontSize: 'clamp(18px, 3vw, 28px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.025em', lineHeight: 1.15, marginBottom: 8, animation: 'cinSlideUp 0.5s cubic-bezier(0.16,1,0.3,1) both' }}
                >
                  {currentPhoto.title}
                </div>
                {/* Caption (if different from title) */}
                {currentPhoto.caption && currentPhoto.caption !== currentPhoto.title && (
                  <div
                    key={`cap-${cinematicIdx}`}
                    style={{ fontSize: 'clamp(12px, 1.7vw, 15px)', color: 'rgba(255,255,255,0.58)', fontStyle: 'italic', lineHeight: 1.6, marginBottom: 10, animation: 'cinSlideUp 0.5s 0.07s cubic-bezier(0.16,1,0.3,1) both' }}
                  >
                    {currentPhoto.caption}
                  </div>
                )}
                {/* Meta row */}
                <div
                  key={`meta-${cinematicIdx}`}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 14, animation: 'cinSlideUp 0.5s 0.14s cubic-bezier(0.16,1,0.3,1) both' }}
                >
                  {currentPhoto.takenDate && (
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Calendar size={10} /> {fmtDate(currentPhoto.takenDate)}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                    by {currentPhoto.uploadedBy}
                  </span>
                </div>
              </div>

              {/* Play / pause */}
              <button
                onClick={() => setIsPlaying(p => !p)}
                style={{ background: 'rgba(255,255,255,0.13)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: '50%', color: '#fff', cursor: 'pointer', width: 52, height: 52, minHeight: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                {isPlaying ? <Pause size={18} /> : <Play size={18} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
